import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";

import { useTwilioDevice } from "Hooks/useTwilioDevice/useTwilioDevice";
import { selectConferenceSnapshotBarges } from "Services/state/conferences/ConferenceSnapshotSelectors";
import { clearConferenceSnapshot } from "Services/state/conferences/ConferenceSnapshotSlice";
import { useRVDispatch } from "Services/state/Storage";

import { BUTTONS_MESSAGES, type CallBarButtonMessagesFactory } from "../../CallBar.consts";
import { logger } from "../../CallBar.utils";

type HangUpCallFn = (alreadyHangingUp?: boolean) => void;

export type UseHangUpCallResult = {
    hangUpCall: HangUpCallFn;
    hangUpButtonMessages: CallBarButtonMessagesFactory;
};

const useHangUpCallData = () => {
    const snapshotBarges = useSelector(selectConferenceSnapshotBarges);

    const bargingParticipantNames = useMemo(() => {
        return snapshotBarges.map(({ label }) => {
            return label;
        });
    }, [snapshotBarges]);

    return { bargingParticipantNames };
};

export const useCallActionHangup = (): UseHangUpCallResult => {
    const dispatch = useRVDispatch();
    const { bargingParticipantNames } = useHangUpCallData();
    const { device } = useTwilioDevice();

    const hangUpCall = useCallback<HangUpCallFn>(
        (alreadyHangingUp) => {
            logger.log("Attempting to hang up", { device, param: alreadyHangingUp });
            dispatch(clearConferenceSnapshot());
            device?.disconnectAll();
        },
        [device, dispatch]
    );
    const hangUpButtonMessages = useMemo(() => {
        if (bargingParticipantNames.length == 0) {
            return BUTTONS_MESSAGES.hangUp;
        }

        let title: string;

        if (bargingParticipantNames.length == 1) {
            const [firstParticipant] = bargingParticipantNames;
            title = `${firstParticipant} is barging. Ending this call will end it for all parties.`;
        } else {
            const [firstParticipant, ...otherParticipants] = bargingParticipantNames;
            const otherParticipantsAreMoreThanOne = otherParticipants.length > 1;
            const label = `${firstParticipant} + ${otherParticipants.length} other${
                otherParticipantsAreMoreThanOne ? "s" : ""
            }`;
            title = `${label} are barging. Ending this call will end it for all parties.`;
        }

        return {
            ...BUTTONS_MESSAGES.hangUp,
            title: () => title,
        };
    }, [bargingParticipantNames]);

    return { hangUpCall, hangUpButtonMessages };
};
